import type { BiomassDataRow } from '../../utils/productionUnitTable'

/**
 * v2.2 findings.md #24 (follow-up to #9): a row can legitimately be BOTH a
 * count entry and a sampling entry on the same day — e.g. the initial stock
 * count and the first weight sample, captured together. `isCountRow` is
 * intentionally EXCLUSIVE (used to decide whether a row belongs in the
 * Sampling list *instead of* Count) — do not reuse it to decide "does this
 * row have count data" (that's `hasStockSignal` below). #9's original fix
 * added the `fishStock` fallback here and introduced this same exclusivity
 * in the same change, which is exactly what caused #24 — a row with both
 * signals silently dropped out of the Conteo recent-cards list. Extracted
 * out of `Biomass.tsx` (which has no RTL test harness — see
 * docs/architecture-concerns.md invariant I-4) specifically so this
 * classification logic can be unit-tested directly instead of only through
 * a pre-computed mock prop, which is how it slipped through twice.
 */
export const isCountRow = (row: BiomassDataRow): boolean => {
  const rowAny = row as Record<string, unknown>
  if (String(rowAny.stockMethod ?? '').trim()) return true

  const hasSamplingSignals = Boolean(
    String(row.weight ?? '').trim()
    || String(row.dailyMortality ?? '').trim()
    || String(rowAny.harvestKg ?? '').trim()
    || String(rowAny.harvestCount ?? '').trim()
  )
  if (hasSamplingSignals) return false

  return Boolean(String(row.fishStock ?? '').trim())
}

/** Inclusive counterpart used by `recentCountRows`: does this row have real
 * stock data at all, regardless of what else it also carries. */
export const hasStockSignal = (row: BiomassDataRow): boolean => {
  const rowAny = row as Record<string, unknown>
  return Boolean(String(rowAny.stockMethod ?? '').trim()) || Boolean(String(row.fishStock ?? '').trim())
}

/**
 * Findings #193: does the row belong in "Muestreo y mortalidad"? A row with
 * weight or mortality does — even when it is also a count (a stocking day's
 * first mortality used to show only under Conteo, i.e. nowhere as mortality).
 * A pure count row (stock only) stays out; harvest rows have their own list.
 */
export const belongsInSamplingList = (row: BiomassDataRow): boolean => {
  const rowAny = row as Record<string, unknown>
  if (String(rowAny.harvestKg ?? '').trim() || String(rowAny.harvestCount ?? '').trim()) return false
  const hasSampling = Boolean(String(row.weight ?? '').trim() || String(row.dailyMortality ?? '').trim())
  if (isCountRow(row)) return hasSampling
  return hasSampling || Boolean(String(row.fishStock ?? '').trim())
}
